import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ArrowRight } from 'lucide-react';
import { mockData } from '../../mock';
import MeshNetwork from '../MeshNetwork';
import PortfolioShowcase from '../PortfolioShowcase';

const Hero = ({ 
  headingRef, 
  handleHeadingMouseMove, 
  isHeadingHovered, 
  setIsHeadingHovered, 
  mousePosition, 
  scrollToContact, 
  scrollToPortfolio,
  scrollY 
}) => {
  return (
    <section className="relative pt-36 pb-36 px-6 bg-[#0B0F1A] overflow-hidden min-h-screen flex items-center">
      {/* Animated Mesh Network Background */}
      <MeshNetwork mousePosition={mousePosition} scrollY={scrollY || 0} />
      
      {/* Background gradient spots */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#2F6BFF]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#4DA3FF]/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="text-center space-y-8">
          <div className="inline-block animate-fadeInUpStagger mt-28" style={{ animationDelay: '0s', opacity: 0 }}>
            <div className="inline-flex items-center justify-center px-5 py-2 rounded-xl bg-gradient-to-r from-[#2F6BFF]/20 via-[#3b82f6]/20 to-[#4DA3FF]/20 border border-[#2F6BFF]/30 backdrop-blur-sm shadow-lg shadow-[#2F6BFF]/20 hover:shadow-xl hover:shadow-[#2F6BFF]/30 transition-all duration-300">
              <span className="text-sm font-medium text-white/95 tracking-wide whitespace-nowrap">
                Web Designer • London
              </span>
            </div>
          </div>
          <h1 
            ref={headingRef}
            className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-white mt-0 md:mt-8 max-w-5xl mx-auto cursor-default relative animate-fadeInUp"
            onMouseMove={handleHeadingMouseMove}
            onMouseEnter={() => setIsHeadingHovered(true)}
            onMouseLeave={() => setIsHeadingHovered(false)}
            style={{
              position: 'relative',
              color: '#ffffff',
              textShadow: isHeadingHovered 
                ? `${(mousePosition.x - 50) * 0.1}px ${(mousePosition.y - 50) * 0.1}px 40px rgba(47, 107, 255, 0.6)`
                : '0 0 20px rgba(47, 107, 255, 0.2)',
              transition: 'text-shadow 0.1s ease'
            }}
          >
            {mockData.hero.headline}
          </h1>
          <p className="text-xl md:text-2xl text-[#9AA3B2] max-w-3xl mx-auto leading-relaxed animate-fadeInUpStagger" style={{ animationDelay: '0.2s', opacity: 0 }}>
            {mockData.hero.subheadline}
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center mt-10 animate-fadeInUpStagger" style={{ animationDelay: '0.4s', opacity: 0 }}>
            <Button 
              onClick={scrollToContact}
              size="lg" 
              className="w-full sm:w-auto bg-gradient-to-r from-[#2F6BFF] to-[#4DA3FF] hover:from-[#4DA3FF] hover:to-[#2F6BFF] text-white text-lg px-10 py-7 font-semibold hover:scale-105 transition-all duration-300 shadow-xl shadow-[#2F6BFF]/30 hover:shadow-2xl hover:shadow-[#4DA3FF]/40"
              style={{
                boxShadow: '0 8px 24px rgba(47, 107, 255, 0.3), 0 16px 48px rgba(77, 163, 255, 0.2)'
              }}
            >
              {mockData.hero.ctaText}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={scrollToPortfolio}
              size="lg" 
              variant="outline" 
              className="w-full sm:w-auto border-2 border-[#2F6BFF] text-[#2F6BFF] hover:bg-[#2F6BFF]/10 text-lg px-10 py-7 font-semibold hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-[#2F6BFF]/20"
            >
              {mockData.hero.secondaryCta}
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-10 animate-fadeInUpStagger" style={{ animationDelay: '0.6s', opacity: 0 }}>
            {mockData.hero.trustBadges.map((badge, index) => (
              <div key={index} className="inline-flex items-center justify-center gap-2 px-4 py-2 h-10 bg-[#2F6BFF]/10 rounded-full border border-[#2F6BFF]/20">
                <span className="text-[#4DA3FF] text-lg leading-none">•</span>
                <span className="text-sm font-medium text-[#F5F7FF] whitespace-nowrap">{badge}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Floating 3D Product Preview */}
        <PortfolioShowcase mousePosition={mousePosition} />
      </div>
    </section>
  );
};

export default Hero;
